import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  UseFormTrigger,
  UseFormClearErrors,
  FieldErrors,
} from 'react-hook-form';

import { ROUTE_DEFINITIONS } from '@/routes';
import { monthlyCutsService } from '@/api/domains/attendance';
import { monthlyCutsYearsService } from '@/api/domains/catalog/monthlyCutsYears/monthlyCutsYears.service';
import { useUI } from '@/hooks/ui/useUI';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { useTranslation } from '@/i18n';
import { getMonthOptions } from '@/utils/dateRange';
import { processBackendValidationErrors } from '@/utils/formValidationErrors';
import {
  editMonthlyCutSchema,
  type EditMonthlyCutFormData,
} from '@/schemas/attendance/editMonthlyCutForm';
import {
  useMonthlyCutGroupsTransfer,
  type UseMonthlyCutGroupsTransferResult,
} from './useMonthlyCutGroupsTransfer';
import type { MonthlyCutTabId } from './useCreateMonthlyCut';


// Estado de corte que bloquea la edición (espejo de la regla backend CONFLICT_MONTHLY_CUT_CLOSED).
const CLOSED_STATE = 'Cerrado';

// Campos de la pestaña General (para enrutar el foco a la pestaña con error).
const GENERAL_FIELDS: (keyof EditMonthlyCutFormData)[] = [
  'year', 'month', 'startDate', 'endDate',
];

export interface EditMonthlyCutFormSubset {
  register: UseFormRegister<EditMonthlyCutFormData>;
  setValue: UseFormSetValue<EditMonthlyCutFormData>;
  watch: UseFormWatch<EditMonthlyCutFormData>;
  trigger: UseFormTrigger<EditMonthlyCutFormData>;
  clearErrors: UseFormClearErrors<EditMonthlyCutFormData>;
  errors: FieldErrors<EditMonthlyCutFormData>;
}

export interface UseEditMonthlyCutResult {
  permissionsLoaded: boolean;
  canEdit: boolean;
  screenCode: string;
  isLoading: boolean;
  isSubmitting: boolean;
  // El corte existe pero está Cerrado: la página muestra el mensaje de bloqueo en vez del form.
  isClosed: boolean;
  // No se pudo cargar el detalle (no existe / error): la página puede ocultar el form.
  loadFailed: boolean;
  activeTab: MonthlyCutTabId;
  setActiveTab: (tab: MonthlyCutTabId) => void;
  form: EditMonthlyCutFormSubset;
  // Id del corte en edición (solo lectura).
  cutId: number;
  // Empresa del corte (solo lectura, no editable).
  companyName: string;
  // Valor original del switch holding (solo lectura en edición).
  applyToAllCompanies: boolean;
  yearOptions: { value: string; label: string }[];
  monthOptions: { value: string; label: string }[];
  groups: UseMonthlyCutGroupsTransferResult;
  handleCancel: () => void;
  handleUpdate: () => void;
}

export function useEditMonthlyCut(): UseEditMonthlyCutResult {
  const { t: translate } = useTranslation();
  const { showSuccess, showWarning, showApiError, showNotification } = useUI();
  const navigate = useNavigate();
  const { id: idParam } = useParams<{ id: string }>();
  const monthlyCutId = Number(idParam);

  const screenCode = ROUTE_DEFINITIONS.CORTE_MENSUAL.code ?? '';
  const { isLoaded: permissionsLoaded, canEdit } = usePagePermissions(screenCode);
  // Solo cargar detalle/catálogos/agrupaciones cuando el permiso está resuelto y el usuario
  // puede editar: evita requests innecesarios y toasts de error al entrar por URL directa sin
  // permiso.
  const hasValidId = Number.isFinite(monthlyCutId) && monthlyCutId > 0;
  const canLoadData = permissionsLoaded && canEdit && hasValidId;

  const [activeTab, setActiveTab] = useState<MonthlyCutTabId>('general');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(true);
  const [isClosed, setIsClosed] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [applyToAllCompanies, setApplyToAllCompanies] = useState(false);
  // El selector dual solo precarga asignadas cuando el corte es editable (Abierto).
  const [groupsMonthlyCutId, setGroupsMonthlyCutId] = useState<number | undefined>(undefined);

  const methods = useForm<EditMonthlyCutFormData>({
    resolver: zodResolver(editMonthlyCutSchema),
    mode: 'onSubmit',
    defaultValues: {
      id: 0,
      year: null,
      month: null,
      startDate: '',
      endDate: '',
      groupIds: [],
    },
  });

  // ── Catálogos ───────────────────────────────────────────────────────────
  const [years, setYears] = useState<number[]>([]);
  const [yearsLoading, setYearsLoading] = useState(true);
  useEffect(() => {
    if (!canLoadData) return;
    const controller = new AbortController();
    const loadYears = async () => {
      setYearsLoading(true);
      const response = await monthlyCutsYearsService.getYears(controller.signal);
      if (response.error?.code === 'ABORTED') return;
      if (response.success && response.data) {
        setYears(response.data.map((item) => item.year));
      } else {
        showApiError(response, translate('attendance:monthlyCutCreate.messages.yearsLoadError'));
      }
      setYearsLoading(false);
    };
    void loadYears();
    return () => controller.abort();
  }, [canLoadData, showApiError, translate]);

  const yearOptions = useMemo(
    () => years.map((year) => ({ value: String(year), label: String(year) })),
    [years],
  );

  const monthOptions = useMemo(() => getMonthOptions(translate), [translate]);

  // ── Carga del detalle (precarga del formulario) ─────────────────────────
  useEffect(() => {
    if (!canLoadData) return;
    const controller = new AbortController();
    const loadDetail = async () => {
      setIsDetailLoading(true);
      const response = await monthlyCutsService.getDetail(monthlyCutId, controller.signal);
      if (response.error?.code === 'ABORTED') return;

      if (!response.success || !response.data) {
        setLoadFailed(true);
        showApiError(response, translate('attendance:monthlyCutEdit.messages.loadError'));
        setIsDetailLoading(false);
        return;
      }

      const detail = response.data;
      setCompanyName(detail.companyName);
      setApplyToAllCompanies(detail.applyToAllCompanies);

      if (detail.state === CLOSED_STATE) {
        // Corte cerrado: no editable. La página muestra el mensaje de bloqueo.
        setIsClosed(true);
        setIsDetailLoading(false);
        return;
      }

      methods.reset({
        id: detail.id,
        year: detail.year,
        month: detail.month,
        startDate: detail.startDate,
        endDate: detail.endDate,
        groupIds: detail.groupIds,
      });
      // Dispara la precarga de agrupaciones asignadas solo para cortes editables.
      setGroupsMonthlyCutId(detail.id);
      setIsDetailLoading(false);
    };
    void loadDetail();
    return () => controller.abort();
  }, [canLoadData, monthlyCutId, methods, showApiError, translate]);

  // ── Agrupación (transfer panel) ─────────────────────────────────────────
  const groups = useMonthlyCutGroupsTransfer({
    enabled: canLoadData && !isClosed && groupsMonthlyCutId !== undefined,
    monthlyCutId: groupsMonthlyCutId,
  });

  // Mantiene el campo del formulario sincronizado con la lista de asignadas.
  useEffect(() => {
    methods.setValue('groupIds', groups.assignedIds);
    if (groups.assignedIds.length > 0) {
      methods.clearErrors('groupIds');
    }
  }, [groups.assignedIds, methods]);

  // ── Submit ──────────────────────────────────────────────────────────────
  const fieldMapping = useMemo(() => {
    const formInstance = { setError: methods.setError };
    return {
      year: { tab: 'general' as MonthlyCutTabId, formInstance },
      month: { tab: 'general' as MonthlyCutTabId, formInstance },
      startDate: { tab: 'general' as MonthlyCutTabId, formInstance },
      endDate: { tab: 'general' as MonthlyCutTabId, formInstance },
      groupIds: { tab: 'agrupacion' as MonthlyCutTabId, formInstance },
    };
  }, [methods]);

  const handleCancel = useCallback(() => {
    void navigate(ROUTE_DEFINITIONS.CORTE_MENSUAL.path);
  }, [navigate]);

  const handleUpdate = useCallback(async () => {
    // Garantiza que groupIds refleje el estado actual del panel antes de validar.
    methods.setValue('groupIds', groups.assignedIds);

    const generalValid = await methods.trigger(GENERAL_FIELDS);
    if (!generalValid) {
      setActiveTab('general');
      return;
    }
    const groupsValid = await methods.trigger('groupIds');
    if (!groupsValid) {
      setActiveTab('agrupacion');
      return;
    }

    setIsSubmitting(true);
    const values = methods.getValues();

    const response = await monthlyCutsService.update({
      id: values.id,
      year: values.year as number,
      month: values.month as number,
      startDate: values.startDate,
      endDate: values.endDate,
      groupIds: values.groupIds,
    });

    if (!response.success) {
      if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
        processBackendValidationErrors(
          response.error.validationErrors,
          fieldMapping,
          (tab) => setActiveTab(tab),
        );
        // Notificación genérica para garantizar feedback aunque ningún fieldName calce con el
        // fieldMapping (p. ej. diferencia de casing del backend); evita que Guardar "no haga nada".
        showNotification('error', translate('common:validation.formErrors'));
        setIsSubmitting(false);
        return;
      }
      showApiError(response, translate('attendance:monthlyCutEdit.messages.editError'));
      setIsSubmitting(false);
      return;
    }

    showSuccess(translate('attendance:monthlyCutEdit.messages.editSuccess'));
    // Solo alertar cuando la empresa SÍ tiene REX+ y aun así no se re-sincronizó tras editar.
    if (response.data && response.data.rexApplicable && !response.data.rexSynced) {
      showWarning(translate('attendance:monthlyCutEdit.messages.rexNotSynced'));
    }
    void navigate(ROUTE_DEFINITIONS.CORTE_MENSUAL.path);
  }, [
    methods, groups.assignedIds, fieldMapping,
    showApiError, showSuccess, showWarning, showNotification, translate, navigate,
  ]);

  const form: EditMonthlyCutFormSubset = {
    register: methods.register,
    setValue: methods.setValue,
    watch: methods.watch,
    trigger: methods.trigger,
    clearErrors: methods.clearErrors,
    errors: methods.formState.errors,
  };

  return {
    permissionsLoaded,
    canEdit,
    screenCode,
    isLoading: isDetailLoading || yearsLoading,
    isSubmitting,
    isClosed,
    loadFailed,
    activeTab,
    setActiveTab,
    form,
    cutId: monthlyCutId,
    companyName,
    applyToAllCompanies,
    yearOptions,
    monthOptions,
    groups,
    handleCancel,
    handleUpdate,
  };
}

export default useEditMonthlyCut;
